import React, { useState, useEffect } from "react";
import { Search, Edit, Trash2, Eye, MoreHorizontal, Mail, MessageSquare, CheckSquare, Square, Sparkles, X } from "lucide-react";
import "./Customers.css";
import Template from "../../Template";
import Spinner, { ButtonLoader } from "../../../components/uxCollections/Loaders";
import noCustomer from "../../../assetsStatic/noCustomer.png";
import { fetchCustomerUrl, sendEmailCampaignUrl } from "../../../api/endpoints";
import { useSelector } from "react-redux";
import CustomerSkeleton from "../../../components/uxCollections/skeletonLoader/customer/CustomerSkeletion";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";
import CustomerDetailOverlay from "./CustomerdetailsOverlay";
import { Pagination } from "../../productCatelog/ProductCatelog";
import Loader from "../../../components/uxCollections/storelensSpiner/StorelenseSpinner";
/* import { Pagination } from "../../productCatelog/ProductCatelog.jsx"; */

const Customerrs = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStore, setSelectedStore] = useState("all");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectCustomerToView, setSelectCustomerToView] = useState(null);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [campaignType, setCampaignType] = useState("email");
  const [campaignMessage, setCampaignMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Simulate loading state
  const [customers, setCustomers] = useState([]);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const { user } = useSelector((state) => state.webStoreUser);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (pageNo = 1) => {
    try {
      setIsLoading(true);
      const url = `${fetchCustomerUrl}?page=${pageNo}&limit=20`; // Add pagination parameters

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (response.status === 404) {
        setCustomers([]);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch customers");
      }

      const { allCustomers, pagination } = await response.json();
      setCustomers(allCustomers || []);
      setPage(pagination.page);
      setTotalPages(pagination.totalPages);
      setTotalCustomers(pagination.total);
      console.log("Fetched Customers:", allCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      uiAlert("Failed to load customers. Please try again later.", "error");
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.storepurchasedFrom.storeName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStore = selectedStore === "all" || customer.storepurchasedFrom.storeName === selectedStore;

    return matchesSearch && matchesStore;
  });

  const stores = Array.from(new Set(customers.map((customer) => customer.storepurchasedFrom.storeName)));

  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers((prev) => (prev.includes(customerId) ? prev.filter((id) => id !== customerId) : [...prev, customerId]));
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map((customer) => customer.customerID));
    }
  };

  const handleSendCampaign = (type) => {
    if (selectedCustomers.length === 0) {
      uiAlert("Please select customers to send campaign", "warning");
      return;
    }

    setCampaignType(type);
    setCampaignMessage("");
    setCampaignDialogOpen(true);
  };

  const generateAIContent = () => {
    const suggestions = {
      email: [
        "🎉 Exclusive offer just for you! Get 20% off your next purchase. Limited time only!",
        "We miss you! Come back and enjoy special savings on your favorite items.",
        "New arrivals are here! Be the first to shop our latest collection with early access.",
        "Your favorites are almost gone! Grab them before they're sold out again.",
        "Flash Sale Alert ⚡ Up to 50% off today only. Don’t miss out!",
        "Thank you for being part of our community! Here’s a 10% gift from us to you.",
        "You’ve got great taste 👀 — here are handpicked items we think you’ll love.",
        "New season, new styles 🍂 Refresh your wardrobe with our latest drops.",
        "It’s back! Our most-loved product just restocked. Order before it’s gone again!",
        "Surprise 🎁 A mystery discount is waiting for you — open to reveal your offer.",
        "Early access just for you 🌟 Shop our exclusive VIP collection before anyone else.",
        "Limited edition alert 🚨 These won’t be around for long. Get yours today!",
        "Make it yours — personalized recommendations based on what you love.",
        "The countdown is on ⏰ 24 hours left to enjoy sitewide savings!",
        "Your loyalty deserves rewards ❤️ Earn double points this weekend only.",
        "Bundle and save 🛍️ Create your perfect combo and save up to 30%.",
        "Ready for a glow-up? ✨ Discover our newest beauty essentials.",
        "Weekend deals you can’t resist 😍 Shop your favorites at special prices.",
        "Refer a friend, get rewarded 💌 Give 15%, get 15% — it’s that easy!",
        "You’re invited 🎈 Join our exclusive online event and win special prizes.",
        "Top picks just for you 🌸 See what’s trending this week.",
        "Final call 🚨 Sale ends tonight — grab your favorites while you can!",
        "Welcome to the family 🙌 Enjoy your first order with 15% off.",
        "We value your feedback 💬 Tell us what you think and get a reward.",
        "Happy Birthday 🎂 A little gift from us to make your day even better.",
        "New year, new you ✨ Kickstart the year with our must-have collections.",
        "Hot right now 🔥 Trending products everyone’s talking about!",
        "Free shipping weekend 🚚 No minimum order required. Shop now!",
        "Sneak peek 👀 See what’s launching next week before anyone else.",
        "Your opinion matters 🗳️ Help shape our next collection — take our quick survey!",
      ],
      sms: [
        "🔥 Flash Sale! 20% off today only. Use code SAVE20. Shop now!",
        "Hi! Your favorite items are back in stock. Don't miss out!",
        "Special offer for you: Buy 2 get 1 FREE. Valid until midnight!",
      ],
    };

    const randomSuggestion = suggestions[campaignType][Math.floor(Math.random() * suggestions[campaignType].length)];
    setCampaignMessage(randomSuggestion);
  };

  const sendCampaign = async () => {
    if (!campaignMessage.trim()) {
      uiAlert("Please enter a campaign message", "warning");
      return;
    }

    // user control
    if (user.userType.userType.toLowerCase().includes("basic")) {
      uiAlert("Your current plan does not allow sending campaigns. Please upgrade your plan to access this feature.", "error");
      return;
    }

    // disable smms

    if (campaignType === "sms") {
      uiAlert("SMS campaign is not available at the moment.");
      return;
    }

    setSendingCampaign(true);

    const selectedCustomerNames = customers.filter((customer) => selectedCustomers.includes(customer.customerID)).map((customer) => customer.fullName);

    console.log(selectedCustomers, "Sending campaign message:", campaignMessage);

    // filter only customers with email or phone based on campaign type

    // regex for valid email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    let customersWithValidEmail = customers
      .filter(
        (customer) => selectedCustomers.includes(customer.customerID) && customer.email && customer.email !== "No Email" && emailRegex.test(customer.email),
      )
      .map((customer) => customer.customerID);

    console.log(customersWithValidEmail, "customers with valid email");

    try {
      let response = await fetch(campaignType === "email" ? sendEmailCampaignUrl : "/api/sendSmsCampaign", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({
          selectedCustomersID: customersWithValidEmail,
          message: campaignMessage,
        }),
      });
      if (!response.ok) {
        uiAlert("Failed to send campaign. Please try again later with another customer", "error");
      }
      const { message, failedEmails } = await response.json();
      uiAlert(`${message}.${failedEmails.length > 0 ? `Failed to send to ${failedEmails.length} customers.` : ""} `, "success");
    } catch (error) {
      console.error("Error sending campaign:", error);
    } finally {
      setSendingCampaign(false);
    }

    setCampaignDialogOpen(false);
    setCampaignMessage("");
  };

  const getCustomerInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "Active":
        return "default";
      case "away":
        return "secondary";
      case "Inactive":
        return "destructive";
      default:
        return "default";
    }
  };

  return (
    <Template>
      <>
        <div className='customers-page'>
          <div className='customers-container'>
            {/* Header */}
            <div className='customers-header'>
              <h1 className='customers-title'>Customer Management</h1>
              <p className='customers-subtitle'>
                You have <b>{totalCustomers.toLocaleString()}</b> customers currently{" "}
              </p>
            </div>

            {/* Controls */}
            {isLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 260px)" }}>
                <Loader size={70} />
              </div>
            ) : (
              <>
                <div className='customers-controls'>
                  <div className='search-filter-row'>
                    <div className='search-container1'>
                      <Search className='search-icon' />
                      <input
                        type='text'
                        placeholder='Search customers...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className='search-input'
                      />
                    </div>
                    <select value={selectedStore} onChange={(e) => setSelectedStore(e.target.value)} className='store-select'>
                      <option value='all'>All Stores</option>
                      {stores.map((store) => (
                        <option key={store} value={store}>
                          {store}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selection and Campaign Actions */}
                  <div className='selection-actions'>
                    <div className='select-all-container'>
                      <input
                        type='checkbox'
                        checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                        onChange={toggleSelectAll}
                        className='checkbox'
                      />
                      <span className='select-all-text'>Select All ({selectedCustomers.length} selected)</span>
                    </div>

                    {selectedCustomers.length > 0 && (
                      <div className='campaign-buttons'>
                        <button onClick={() => handleSendCampaign("email")} className='btn btn-email'>
                          <Mail className='h-4 w-4' />
                          Send Email Campaign
                        </button>
                        <button onClick={() => handleSendCampaign("sms")} className='btn btn-sms'>
                          <MessageSquare className='h-4 w-4' />
                          Send SMS Campaign
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Grid */}
                <div className='customers-grid'>
                  {filteredCustomers.map((customer) => (
                    <div
                      style={{ position: "relative" }}
                      onClick={() => {
                        toggleCustomerSelection(customer.customerID);
                      }}
                      key={customer.customerID}
                      className='customer-card'
                    >
                      {/* Card Header */}
                      <div className='card-header'>
                        <div className='card-header-content'>
                          <input type='checkbox' checked={selectedCustomers.includes(customer.customerID)} className='checkbox' />
                          <div className='card-header-text'>
                            <h3 className='customer-name'>{customer.fullName}</h3>
                            <p className='customer-company'>{customer.purchaseChannel === "inStore" ? "in store purchase" : customer.purchaseChannel}</p>
                          </div>
                        </div>
                        {/*    <button className='cusdropdown-trigger'>
                          <MoreHorizontal className='h-4 w-4' />
                        </button> */}
                      </div>

                      {/* Customer Info */}
                      <div className='customer-info'>
                        {/*  <div className='info-item'>
                          <p className='info-label'>Email</p>
                          <p className='info-value'>{customer.email}</p>
                        </div> */}
                        <div className='info-item'>
                          <p className='info-label'>Phone</p>
                          <p className='info-value'>{customer.phoneNumber}</p>
                        </div>
                        <div className='info-item'>
                          <p className='info-label'>Store</p>
                          <p className='info-value'>{customer.storepurchasedFrom.storeName}</p>
                        </div>
                        <div className='info-item'>
                          <p className='info-label'>Last Contact</p>
                          <p className='info-value'>{customer.lastPurchaseDate.split("T")[0] || "yesterday"}</p>
                        </div>

                        <TapToViewInfo customer={customer} setSelectCustomerToView={setSelectCustomerToView} />
                      </div>

                      {/* Card Footer */}
                      {customer.amountOwned ? (
                        <div className='card-footer'>
                          <span className={`badge badge-${getStatusBadgeVariant("Inactive")}`}>{"Owns me"}</span>
                          <span style={{ color: "#dc2626" }} className='customer-value'>
                            {user.userCountryCurrencySymbol} {customer.amountOwned.toLocaleString() || "£ 100"}
                          </span>
                        </div>
                      ) : (
                        <div className='card-footer'>
                          <span className={`badge badge-${getStatusBadgeVariant(customer.status ? customer.status : "Active")}`}>
                            {customer.status ? customer.status : "Active"}
                          </span>
                          <span className='customer-value'>
                            {user.userCountryCurrencySymbol} {customer.totalPurchaseAmount.toLocaleString() || "£ 100"}
                          </span>
                        </div>
                      )}
                      {customer.isNewCustomer && (
                        <span
                          style={{
                            position: "absolute",
                            top: "0",
                            right: "0",
                            padding: "5px 10px",
                            backgroundColor: "#f0f0f0",
                            borderRadius: "0 0.7rem 0 5px",
                            fontSize: "12px",
                            color: "#555",
                          }}
                          className='new-customer-badge'
                        >
                          new
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Empty State */}
            {!isLoading && filteredCustomers.length === 0 && (
              <div className='empty-state'>
                <div className='empty-state-card'>
                  <img style={{ width: "150px" }} src={noCustomer} />
                  <h3 className='empty-title'>No customers found</h3>
                  <p className='empty-description'>Try adjusting your search criteria.</p>
                  <p className='empty-hint'>Otherwise when you have sold to a customer, they will appear here.</p>
                </div>
              </div>
            )}

            {/* Campaign Dialog */}
            {campaignDialogOpen && (
              <div className='modal-overlay' onClick={() => setCampaignDialogOpen(false)}>
                <div className='modal-content' onClick={(e) => e.stopPropagation()}>
                  <button className='close-button' onClick={() => setCampaignDialogOpen(false)}>
                    <X className='close-icon' />
                  </button>

                  <div className='modal-header'>
                    <h2 className='modal-title'>Send {campaignType === "email" ? "Email" : "SMS"} Campaign</h2>
                  </div>

                  {/* Selected Customers */}
                  <div className='recipients-section'>
                    <p className='recipients-label'>To:</p>
                    <div className='recipients-list'>
                      {customers
                        .filter((customer) => selectedCustomers.includes(customer.customerID))
                        .slice(0, 5)
                        .map((customer) => (
                          <div key={customer.customerID} className='recipient-item'>
                            <div className='avatar'>{getCustomerInitials(customer.fullName)}</div>
                            <span className='recipient-name'>{customer.fullName}</span>
                          </div>
                        ))}
                      {selectedCustomers.length > 5 && <div className='overflow-indicator'>+{selectedCustomers.length - 5}</div>}
                    </div>
                  </div>

                  {/* Message Field */}
                  <div className='message-section'>
                    <div className='message-container'>
                      <textarea
                        placeholder={`Type your ${campaignType} message here...`}
                        value={campaignMessage}
                        onChange={(e) => setCampaignMessage(e.target.value)}
                        className='message-textarea'
                      />
                      <button onClick={generateAIContent} className='ai-button' title='Generate with AI'>
                        <Sparkles className='ai-icon' />
                      </button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className='modal-actions'>
                    <button onClick={() => setCampaignDialogOpen(false)} className='btn btn-secondary'>
                      Cancel
                    </button>
                    {sendingCampaign ? (
                      <button style={{ opacity: "0.7", pointerEvents: "none" }} className={`btn ${campaignType === "email" ? "btn-email" : "btn-sms"}`}>
                        <ButtonLoader size={20} color='#fff' />
                      </button>
                    ) : (
                      <button onClick={sendCampaign} className={`btn ${campaignType === "email" ? "btn-email" : "btn-sms"}`}>
                        Send {campaignType === "email" ? "Email" : "SMS"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectCustomerToView && (
          <CustomerDetailOverlay
            customer={selectCustomerToView}
            transactions={[]}
            currency={user.userCountryCurrencySymbol}
            onClose={() => setSelectCustomerToView(null)}
            onMessage={(customer) => {
              toggleCustomerSelection(customer.customerID);
              setSelectCustomerToView(null);
            }}
          />
        )}
        <Pagination current={page} total={totalPages} onChange={fetchCustomers} />
      </>
    </Template>
  );
};

export default Customerrs;

const TapToViewInfo = ({ customer, setSelectCustomerToView }) => {
  return (
    <>
      {/* TAP TO VIEW FULL PROFILE */}
      <div
        style={{
          margin: "14px 0 4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
            setSelectCustomerToView(customer);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            background: "#f0f4fa",
            border: "1.5px dashed #c8d4e8",
            borderRadius: 20,
            padding: "7px 16px",
            fontSize: 12,
            fontWeight: 700,
            color: "#6b7a99",
            cursor: "pointer",
            userSelect: "none",
            transition: "background .18s, border-color .18s, color .18s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#dbeafe";
            e.currentTarget.style.borderColor = "#4A90E2";
            e.currentTarget.style.color = "#1e3a5f";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#f0f4fa";
            e.currentTarget.style.borderColor = "#c8d4e8";
            e.currentTarget.style.color = "#6b7a99";
          }}
        >
          {/* Eye icon circle */}
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "#1e3a5f",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
              <circle cx='12' cy='12' r='3' />
            </svg>
          </div>

          <span>Tap to view full profile</span>

          {/* Animated pulse dot */}
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#4A90E2",
              flexShrink: 0,
              animation: "cdpPulse 1.8s ease infinite",
            }}
          />
        </div>
      </div>

      {/* Add this once to your global CSS or a <style> tag */}
      {/* @keyframes cdpPulse {
  0%,100% { opacity: 1; transform: scale(1); }
  50%      { opacity: .45; transform: scale(.65); }
} */}
    </>
  );
};
